import {
    isEmpty,
    isArrayLikeObject,
    isObjectLike,
    filter,
    find,
    map,
    forEach,
    concat,
    includes,
    unionBy,
    intersectionBy,
    isUndefined,
} from 'lodash';

import * as PurchaseMethodsConstants from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import * as dataTypes from 'commons-vehicle/constants/dataTypes.js';
import { CASH_PURCHASE_ONLY_DISCOUNT } from 'commons-vehicle/constants/dataConversions.js';
import { MODEL_TAXES } from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import { VIEW_1 } from 'commons-vehicle/constants/apiDataConversionConstants.js';
import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';

import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import * as FinanceService from 'commons-vehicle/PurchaseMethods/services/FinanceService/FinanceService.js';
import * as CashPurchaseService from 'commons-vehicle/PurchaseMethods/services/CashPurchaseService/CashPurchaseService.js';
import { recalculateTermPayment } from 'commons-vehicle/PurchaseMethods/services/TermService/TermService.js';
import { resetOptionFromQuoteItem } from 'commons-vehicle/ModelYear/services/OptionService/OptionService.js';

const { VEHICLE } = VIEW_1;
/* eslint no-bitwise: 0 */

export function recalculatePayments(purchaseMethods, paymentCalculationParameters) {
    return purchaseMethods
        .set('taxesIncluded', paymentCalculationParameters.taxesIncluded)
        .set('tradeIn', paymentCalculationParameters.tradeIn)
        .set('lien', paymentCalculationParameters.lien)
        .set('exteriorColourRetail', paymentCalculationParameters.exteriorColourRetail)
        .set('exteriorColourResidualRetail', paymentCalculationParameters.exteriorColourResidualRetail)
        .set('interiorColourRetail', paymentCalculationParameters.interiorColourRetail)
        .set('interiorColourResidualRetail', paymentCalculationParameters.interiorColourResidualRetail)
        .set('optionsTotalRetail', paymentCalculationParameters.optionsTotalRetail)
        .set('optionsTotalResidualRetail', paymentCalculationParameters.optionsTotalResidualRetail)
        .setIn(
            ['finance', 'terms'],
            purchaseMethods
                .getIn(['finance', 'terms'])
                .map((term) => recalculateTermPayment(term, paymentCalculationParameters))
        )
        .setIn(
            ['lease', 'terms'],
            purchaseMethods
                .getIn(['lease', 'terms'])
                .map((term) => recalculateTermPayment(term, paymentCalculationParameters))
        )
        .setIn(['finance', 'selectedTerm'], paymentCalculationParameters.selectedFinanceTerm)
        .setIn(['lease', 'selectedTerm'], paymentCalculationParameters.selectedLeaseTerm);
}

export function recalculatePurchaseAmounts(purchaseMethods, amountsCalculationParameters) {
    const retail = purchaseMethods.getIn(['cashPurchase', 'retail']);
    const optionsAdjustment =
        amountsCalculationParameters.exteriorColourRetail +
        amountsCalculationParameters.interiorColourRetail +
        amountsCalculationParameters.optionsTotalRetail;
    const optionsTaxesAdjustment = optionsAdjustment * amountsCalculationParameters.taxesFactor;
    const netTradeInAdjustment =
        amountsCalculationParameters.tradeIn - amountsCalculationParameters.lien > retail + optionsAdjustment
            ? retail + optionsAdjustment
            : amountsCalculationParameters.tradeIn - amountsCalculationParameters.lien;
    const tradeInTaxesAdjustment =
        amountsCalculationParameters.tradeIn > retail + optionsAdjustment
            ? (retail + optionsAdjustment) * amountsCalculationParameters.taxesFactor
            : amountsCalculationParameters.tradeIn * amountsCalculationParameters.taxesFactor;
    return purchaseMethods
        .setIn(['cashPurchase', 'netTradeInAdjustment'], netTradeInAdjustment)
        .setIn(['cashPurchase', 'tradeInTaxesAdjustment'], tradeInTaxesAdjustment)
        .setIn(['cashPurchase', 'optionsAdjustment'], optionsAdjustment)
        .setIn(['cashPurchase', 'optionsTaxesAdjustment'], optionsTaxesAdjustment);
}

export function resetOptionsAdjustment(purchaseMethods) {
    if (isEmpty(purchaseMethods.getAllSelectedOptions())) {
        return purchaseMethods;
    }

    return purchaseMethods.set('cashPurchase', purchaseMethods.cashPurchase.set('optionsAdjustment', 0));
}

export function resetOptionsAmounts(purchaseMethods) {
    const allSelectedOptions = purchaseMethods.getNonConfigurationSelectedOptions();
    const includedOtherFees = purchaseMethods.getOtherFeesAmountIncluded(allSelectedOptions);
    const includedProtection = purchaseMethods.getProtectionsAmountIncluded(allSelectedOptions);

    const includedAccessories = purchaseMethods.getAccessoriesAmountIncluded(allSelectedOptions);

    const optionsRetail = purchaseMethods.getOptionsAmount(allSelectedOptions) - includedAccessories;
    const optionsResidualizingAmount = purchaseMethods.getOptionsResidualizingAmount(allSelectedOptions);
    const otherFeesRetail = purchaseMethods.getOtherFeesAmount(allSelectedOptions, false) - includedOtherFees;
    const protectionsRetail = purchaseMethods.getProtectionsAmount(allSelectedOptions) - includedProtection;
    const dealerDiscounts = purchaseMethods.getDealerDiscountsAmount(allSelectedOptions);
    const { employeeRebateAdjustment } = purchaseMethods;

    let optionsAdjustmentDiscounts = 0;
    if (purchaseMethods.finance.selected || purchaseMethods.lease.selected) {
        optionsAdjustmentDiscounts = purchaseMethods.cashPurchase.optionsAdjustmentDiscounts;
    }
    const optionsAmounts = {
        retail: optionsRetail + otherFeesRetail + protectionsRetail - dealerDiscounts - optionsAdjustmentDiscounts,
        residualRetail: optionsResidualizingAmount,
    };
    if (purchaseMethods.cashPurchase.optionsAdjutmentDiscount === 0) {
        optionsAmounts.retail -= employeeRebateAdjustment;
    }

    let calculationParameters = purchaseMethods.getPaymentCalculationWithOptions(optionsAmounts);
    let newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    newPurchaseMethods = recalculatePayments(newPurchaseMethods, calculationParameters);

    const exteriorColourRetail = purchaseMethods.getExteriorColourAmount(allSelectedOptions);
    const exteriorColourResidualizingAmount = purchaseMethods.getExteriorColourResidualizingAmount(allSelectedOptions);

    const exteriorColourAmounts = {
        retail: exteriorColourRetail,
        residualRetail: exteriorColourResidualizingAmount,
    };

    calculationParameters = newPurchaseMethods.getPaymentCalculationWithExteriorColour(exteriorColourAmounts);
    newPurchaseMethods = recalculatePurchaseAmounts(newPurchaseMethods, calculationParameters);
    newPurchaseMethods = recalculatePayments(newPurchaseMethods, calculationParameters);

    const interiorColourRetail = purchaseMethods.getInteriorColourAmount(allSelectedOptions);
    const interiorColourResidualizingAmount = purchaseMethods.getInteriorColourResidualizingAmount(allSelectedOptions);

    const interiorColourAmounts = {
        retail: interiorColourRetail,
        residualRetail: interiorColourResidualizingAmount,
    };

    calculationParameters = newPurchaseMethods.getPaymentCalculationWithInteriorColour(interiorColourAmounts);
    newPurchaseMethods = recalculatePurchaseAmounts(newPurchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

function resetOptions(purchaseMethods, resetOption, add) {
    const selectedOptions = filter(purchaseMethods.getAllSelectedOptions(), (option) => {
        return option.selectionCode !== resetOption.selectionCode;
    });
    const availableOptions = filter(purchaseMethods.availableOptions, (option) => {
        return option.selectionCode !== resetOption.selectionCode;
    });

    if (add) {
        selectedOptions.push(resetOption.set('selected', true));
    } else {
        availableOptions.push(resetOption.set('selected', false));
    }

    return resetOptionsAmounts(
        purchaseMethods.set('selectedOptions', selectedOptions).set('availableOptions', availableOptions)
    );
}

export function resetPurchaseMethodsFromOriginData(
    dataOrigin,
    originPurchaseMethodsData,
    defaultPurchaseMethod,
    disclaimers
) {
    if (
        isEmpty(originPurchaseMethodsData) ||
        (!isArrayLikeObject(originPurchaseMethodsData) && !isObjectLike(originPurchaseMethodsData))
    ) {
        return new PurchaseMethodsModel();
    }
    let lease = FinanceService.resetFinanceFromOriginData(
        dataOrigin,
        originPurchaseMethodsData,
        PurchaseMethodsConstants.LEASE,
        true
    );
    let finance = FinanceService.resetFinanceFromOriginData(
        dataOrigin,
        originPurchaseMethodsData,
        PurchaseMethodsConstants.FINANCE,
        !lease.selected
    );
    let cashPurchase = CashPurchaseService.resetCashPurchaseFromOriginData(
        dataOrigin,
        originPurchaseMethodsData,
        !lease.selected && !finance.selected
    );

    let taxesFactor = 0;
    let taxCodes = [];
    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            taxesFactor = originPurchaseMethodsData.taxesFactor;
            taxCodes = originPurchaseMethodsData.taxCodes;
            break;
        case DataOrigins.PAYMENT_CALCULATOR:
            taxesFactor = originPurchaseMethodsData.taxesFactor;
            taxCodes = originPurchaseMethodsData.taxCodes;
            break;
        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            taxesFactor = originPurchaseMethodsData[MODEL_TAXES.TAXES_FACTOR];
            taxCodes = originPurchaseMethodsData[MODEL_TAXES.TAX_CODES];
            break;
        case DataOrigins.VEHICLE_PAYMENTS:
            taxesFactor = originPurchaseMethodsData.taxesFactor;
            taxCodes = originPurchaseMethodsData.taxCodes;
            break;
        default:
            break;
    }

    if (defaultPurchaseMethod) {
        lease = lease.set('selected', defaultPurchaseMethod === PurchaseMethodsConstants.LEASE);
        finance = finance.set('selected', defaultPurchaseMethod === PurchaseMethodsConstants.FINANCE);
        cashPurchase = cashPurchase.set(
            'selected',
            defaultPurchaseMethod !== PurchaseMethodsConstants.LEASE &&
                defaultPurchaseMethod !== PurchaseMethodsConstants.FINANCE
        );
    }

    let highRebate = 0;
    let highRebatePurchaseMethod = PURCHASE_METHODS.CASH;
    if (originPurchaseMethodsData[VEHICLE.HIGH_REBATE]) {
        const highRebateObject = originPurchaseMethodsData[VEHICLE.HIGH_REBATE][VEHICLE.HIGH_REBATE_INDEX];
        const highRebateTaxes = highRebateObject[VEHICLE.HIGH_REBATE_REBATES_TAXES] || 0;
        highRebate = highRebateObject[VEHICLE.HIGH_REBATE_RETAIL] + highRebateTaxes;
        highRebatePurchaseMethod = highRebateObject[VEHICLE.HIGH_REBATE_PURCHASE_METHOD];
    }

    const purchaseMethods = new PurchaseMethodsModel({
        taxesFactor,
        taxCodes,
        highRebate,
        highRebatePurchaseMethod,
        lease,
        finance,
        cashPurchase,
    });

    const newPurchaseMethods = disclaimers ? purchaseMethods.set('disclaimers', disclaimers) : purchaseMethods;
    return dataOrigin === DataOrigins.VEHICLE_PAYMENTS
        ? resetOptionsAdjustment(newPurchaseMethods)
        : newPurchaseMethods;
}

export function resetPurchaseMethodsOptionsFromQuoteItems(purchaseMethods, quoteItems, availableQuoteItems) {
    if (!isArrayLikeObject(quoteItems)) {
        return purchaseMethods;
    }

    const selectedQuoteItems = filter(quoteItems, (quoteItem) => {
        const selected = (quoteItem.flag & dataTypes.ITEM_FLAG.SELECTED) > 0;
        const hasDealerCashOnlyDiscount = includes(quoteItem.supplierCodes, CASH_PURCHASE_ONLY_DISCOUNT);
        return selected || hasDealerCashOnlyDiscount;
    });

    const availableItems = map(availableQuoteItems, (quoteItem) => {
        return resetOptionFromQuoteItem(quoteItem, false);
    });

    let includedItemCodes = purchaseMethods.cashPurchase.includedItemsCodes;
    const selectedTerm = purchaseMethods.getSelectedFinanceTerm();
    if (selectedTerm) {
        includedItemCodes = selectedTerm.includedItemsCodes;
    }

    return purchaseMethods.set('availableOptions', availableItems).set(
        'selectedOptions',
        map(selectedQuoteItems, (selectedQuoteItem) => {
            const itemCode = `${selectedQuoteItem.code}>${selectedQuoteItem.supplier}`;
            return resetOptionFromQuoteItem(selectedQuoteItem, includes(includedItemCodes, itemCode));
        })
    );
}

export function resetPurchaseMethodsOptionsFromShowroomVehicleItems(purchaseMethods, items, availableQuoteItems) {
    const configurationOptions = filter(items, (item) => {
        return (item.flag & dataTypes.ITEM_FLAG.CONFIGURATION_SELECTED) > 0;
    }).map((item) => {
        return resetOptionFromQuoteItem(item);
    });

    return resetPurchaseMethodsOptionsFromQuoteItems(purchaseMethods, items, availableQuoteItems).set(
        'configurationOptions',
        configurationOptions
    );
}

function setBestPaymentFinance(purchaseMethods) {
    const bestPaymentFinance = purchaseMethods.getBestPaymentFinance();
    return purchaseMethods
        .setIn(['lease', 'selected'], bestPaymentFinance === PURCHASE_METHODS.LEASE)
        .setIn(['finance', 'selected'], bestPaymentFinance === PURCHASE_METHODS.FINANCE)
        .setIn(['cashPurchase', 'selected'], false);
}

export function selectPurchaseMethod(purchaseMethods, purchaseMethod) {
    return purchaseMethod === PurchaseMethodsConstants.BEST_PAYMENT
        ? setBestPaymentFinance(purchaseMethods)
        : purchaseMethods
              .setIn(['finance', 'selected'], purchaseMethod === PurchaseMethodsConstants.FINANCE)
              .setIn(['lease', 'selected'], purchaseMethod === PurchaseMethodsConstants.LEASE)
              .setIn(['cashPurchase', 'selected'], purchaseMethod === PurchaseMethodsConstants.CASH_PURCHASE);
}

export function selectKMPlan(purchaseMethods, kmPlan) {
    const calculationParameters = purchaseMethods.getPaymentCalculationParameters();
    const selectedFinance = purchaseMethods.getSelectedFinance();
    if (!selectedFinance) {
        return purchaseMethods;
    }
    const newKMPlan = selectedFinance.getKMPlan(kmPlan);
    if (!newKMPlan) {
        return purchaseMethods;
    }
    const newPurchaseMethods = purchaseMethods.getIn(['lease', 'selected'])
        ? purchaseMethods
              .setIn(['lease', 'annualKMAllowed'], newKMPlan.annualKMAllowed)
              .setIn(['lease', 'endTermKMCost'], newKMPlan.endTermKMCost)
              .setIn(['lease', 'terms'], newKMPlan.terms)
        : purchaseMethods
              .setIn(['finance', 'annualKMAllowed'], newKMPlan.annualKMAllowed)
              .setIn(['finance', 'endTermKMCost'], newKMPlan.endTermKMCost)
              .setIn(['finance', 'terms'], newKMPlan.terms);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function selectTerm(purchaseMethods, term) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithTerm(term);
    return recalculatePayments(purchaseMethods, calculationParameters);
}

export function setTargetPayment(purchaseMethods, targetPayment) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithTargetPayment(targetPayment);
    return recalculatePayments(purchaseMethods, calculationParameters);
}

export function selectPaymentFrequency(purchaseMethods, paymentFrequency) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithPaymentFrequency(paymentFrequency);
    return recalculatePayments(purchaseMethods, calculationParameters);
}

export function setCashDown(purchaseMethods, cashDown) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithCashDown(parseFloat(cashDown));
    return recalculatePayments(purchaseMethods, calculationParameters);
}

export function setTradeIn(purchaseMethods, tradeIn) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithTradeIn(tradeIn);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function setLien(purchaseMethods, lien) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithLien(lien);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function setExteriorColourAmounts(purchaseMethods, exteriorColourAmounts) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithExteriorColour(exteriorColourAmounts);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function setInteriorColourAmounts(purchaseMethods, interiorColourAmounts) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithInteriorColour(interiorColourAmounts);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function setOptionsAmounts(purchaseMethods, optionsAmounts) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithOptions(optionsAmounts);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function setTaxesIncluded(purchaseMethods, taxesIncluded) {
    const calculationParameters = purchaseMethods.getPaymentCalculationWithTaxesIncluded(taxesIncluded);
    const newPurchaseMethods = recalculatePurchaseAmounts(purchaseMethods, calculationParameters);
    return recalculatePayments(newPurchaseMethods, calculationParameters);
}

export function selectOptionsResolution(purchaseMethods, resolutionId) {
    return purchaseMethods.setIn(
        ['optionsSelectionResolutions', 'resolutions'],
        map(purchaseMethods.optionsSelectionResolutions.resolutions, (resolution) => {
            return resolution.set('selected', resolution.id === resolutionId);
        })
    );
}

export function setSelectedFinanceBestPaymentTerm(purchaseMethods, maximumTerm) {
    const selectedFinance = purchaseMethods.getSelectedFinance();
    const bestPaymentTerm = selectedFinance ? selectedFinance.getBestPaymentTerm(maximumTerm) : undefined;
    if (!bestPaymentTerm) {
        return purchaseMethods;
    }
    return selectTerm(purchaseMethods, bestPaymentTerm.term);
}

export function setDeliveryFee(purchaseMethods, addFee) {
    const deliveryFee = purchaseMethods.getDeliveryFee();
    if (!deliveryFee) {
        return purchaseMethods;
    }

    return resetOptions(purchaseMethods, deliveryFee, addFee);
}

export function setDeposit(purchaseMethods, deposit) {
    const depositItem = deposit.checkout
        ? purchaseMethods.getCheckoutDeposit()
        : purchaseMethods.getReservationDeposit();
    if (!depositItem) {
        return purchaseMethods;
    }
    const depositAmount = -1 * deposit.override;

    return resetOptions(purchaseMethods, depositItem.set('retail', depositAmount).set('dueAtDelivery', true), true);
}

export function getFormattedCurrencySplit(formattedCurrency) {
    const formattedCurrencySplit = {
        beforeDecimal: '',
        afterDecimal: '',
    };
    let decimalPoint = formattedCurrency.indexOf('.');
    if (decimalPoint === -1) {
        decimalPoint = formattedCurrency.indexOf(',');
    }
    if (decimalPoint > -1) {
        formattedCurrencySplit.beforeDecimal = formattedCurrency.substring(0, decimalPoint);
        formattedCurrencySplit.afterDecimal = formattedCurrency.substring(decimalPoint + 1, formattedCurrency.length);
    } else {
        formattedCurrencySplit.beforeDecimal = formattedCurrency;
    }
    return formattedCurrencySplit;
}

export function resetProtections(purchaseMethods, protections) {
    const selectedOptions = filter(purchaseMethods.getAllSelectedOptions(), (option) => {
        return option.dataType !== dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID;
    });

    let selectedProtections = [];
    forEach(protections, (protection) => {
        selectedProtections = concat(selectedProtections, protection.getSelectedItemsAndPackages());
    });

    return resetOptionsAmounts(purchaseMethods.set('selectedOptions', concat(selectedOptions, selectedProtections)));
}

export function resetSelectedOptions(purchaseMethods, options, configurationOptions) {
    const hasSelectedExtColour = !isUndefined(
        find(options, (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR
            );
        })
    );
    const hasSelectedIntColour = !isUndefined(
        find(options, (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM
            );
        })
    );

    let purchaseMethodsOptions = purchaseMethods.selectedOptions;
    if (hasSelectedExtColour) {
        purchaseMethodsOptions = filter(purchaseMethodsOptions, (purchaseMethodsOption) => {
            return !(
                purchaseMethodsOption.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                purchaseMethodsOption.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR
            );
        });
    }
    if (hasSelectedIntColour) {
        purchaseMethodsOptions = filter(purchaseMethodsOptions, (purchaseMethodsOption) => {
            return !(
                purchaseMethodsOption.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                purchaseMethodsOption.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM
            );
        });
    }
    purchaseMethodsOptions = intersectionBy(options, purchaseMethodsOptions, 'selectionCode');

    return resetOptionsAmounts(
        purchaseMethods
            .set('configurationOptions', configurationOptions)
            .set('selectedOptions', unionBy(purchaseMethodsOptions, options, 'selectionCode'))
    );
}

export function resetSelectedIncentiveRebates(purchaseMethods) {
    const purchaseMethodsOptions = filter(purchaseMethods.selectedOptions, (selectedOption) => {
        return selectedOption.supplier !== dataTypes.SUPPLIER_INCENTIVE || selectedOption.userSelected;
    });

    return purchaseMethods.set('selectedOptions', purchaseMethodsOptions);
}

export default {
    getFormattedCurrencySplit,
    recalculatePayments,
    recalculatePurchaseAmounts,
    resetOptions,
    resetOptionsAmounts,
    resetProtections,
    resetPurchaseMethodsFromOriginData,
    resetPurchaseMethodsOptionsFromQuoteItems,
    resetPurchaseMethodsOptionsFromShowroomVehicleItems,
    resetSelectedOptions,
    resetSelectedIncentiveRebates,
    selectKMPlan,
    selectOptionsResolution,
    selectPaymentFrequency,
    selectPurchaseMethod,
    selectTerm,
    setBestPaymentFinance,
    setCashDown,
    setDeliveryFee,
    setDeposit,
    setExteriorColourAmounts,
    setInteriorColourAmounts,
    setLien,
    setOptionsAmounts,
    setSelectedFinanceBestPaymentTerm,
    setTargetPayment,
    setTaxesIncluded,
    setTradeIn,
    resetOptionsAdjustment,
};
